import { getAlfredApiUrl, toAsciiUploadFilename } from "src/utils";
import {
  AlfredAllConfigs,
  AlfredFiatAccount,
  AlfredDeleteFiatAccountResponse,
  AlfredFiatAccountRequirementCountry,
  AlfredFiatAccountRequirements,
  AlfredPaymentMethod,
  AlfredQuote,
  AlfredSupportedOptionsResponse,
  AlfredSupportedPair,
  AlfredEligibility,
  Bank,
  CreateFiatAccountPayload,
  KycFallback,
  SubmitKycFallbackPayload,
  SubmitKycFallbackResponse,
  CreatePaymentMethodPayload,
  GetBanksParams,
  InitiateDepositPayload,
  InitiateDepositResponse,
  InitiateWithdrawalPayload,
  InitiateWithdrawalResponse,
  QuotePreviewPayload,
} from "./types";

export enum AlfredEndpoints {
  ELIGIBILITY = "/psp/alfred/eligibility",
  CONFIGS = "/psp/alfred/configs",
  SUPPORTED_OPTIONS = "/psp/alfred/supported-options",
  QUOTE_PREVIEW = "/psp/alfred/quote-preview",
  INITIATE_DEPOSIT = "/psp/alfred/initiate-deposit",
  INITIATE_WITHDRAWAL = "/psp/alfred/initiate-withdrawal",
  PAYMENT_METHODS = "/psp/alfred/payment-methods",
  FIAT_ACCOUNTS = "/psp/alfred/fiat-accounts",
  FIAT_ACCOUNT_REQUIREMENTS = "/psp/alfred/fiat-accounts/requirements",
  KYC_FALLBACK = "/psp/alfred/kyc/fallback",
  SESSION = "/psp/alfred/session",
  DEV_EMIT_ONRAMP = "/psp/alfred/__dev/emit-onramp",
  DEV_EMIT_OFFRAMP = "/psp/alfred/__dev/emit-offramp",
  DEV_STATUSES = "/psp/alfred/__dev/statuses",
}

/**
 * Find a supported pair by from/to currency. Use this on the form to
 * derive the min/max amount the user is allowed to type. Returns null
 * when the pair isn't configured at Alfred — the form should show a
 * "pair not supported" state.
 */
export function findPair(
  configs: AlfredAllConfigs,
  fromCurrency: string,
  toCurrency: string,
): AlfredSupportedPair | null {
  const matches = configs.supportedPairs.filter(
    (p) =>
      p.fromCurrency.toUpperCase() === fromCurrency.toUpperCase() &&
      p.toCurrency.toUpperCase() === toCurrency.toUpperCase(),
  );
  if (matches.length === 0) return null;
  return matches.find((p) => p.businessId === null) ?? matches[0];
}

/** Format a customer's Alfred payment method for the bank-picker UI. */
function paymentMethodToBank(pm: AlfredPaymentMethod): Bank {
  const last4 = pm.accountNumber?.slice(-4) ?? "";
  return {
    bankGroupId: pm.paymentMethodId,
    bankGroupName: last4 ? `${pm.bankName} — ****${last4}` : pm.bankName,
    universalBranchCode: pm.countryCode,
  };
}

class AlfredClient {
  private apiUrl: string;
  private authorization = "";

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  /**
   * Forwarded by `useTokenInitializer` once the host app's JWT has
   * been retrieved. Required for endpoints that filter by the user
   * (e.g. `supported-options` narrows fiats to the user's country).
   */
  public setToken(token?: string): void {
    if (token) this.authorization = token;
  }

  /**
   * Talks to the vakotrade backend, which proxies to the Alfred REST
   * API server-side. The widget never holds Alfred's api-secret.
   */
  private async request<T = any>(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    endpoint: string,
    options?: {
      body?: Record<string, any>;
      params?: Record<string, string>;
    }
  ): Promise<T> {
    try {
      const queryString = options?.params ? "?" + new URLSearchParams(options.params).toString() : "";

      const response = await fetch(`${this.apiUrl.replace("/graphql", "")}${endpoint}${queryString}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...(this.authorization ? { Authorization: this.authorization } : {}),
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
      });

      if (!response.ok) {
        // Surface the actionable error message from the body when the
        // BE forwards an Alfred-shape body (`{message, errorCode, ...}`)
        // — show that message verbatim. Plain-text / opaque bodies keep
        // the `HTTP <status>: <statusText>` prefix so the user at least
        // sees the status.
        const errorBody = await response.text();
        let detail: string | null = null;
        try {
          const parsed = JSON.parse(errorBody) as {
            message?: string;
            errorMessage?: string;
            errorCode?: number | string;
          };
          detail = parsed?.message ?? parsed?.errorMessage ?? null;
        } catch {
          // not JSON — keep raw text
        }
        console.warn(`Request failed: ${response.status} ${response.statusText}. ${errorBody}`);
        // `detail` lets callers tell a server-provided message from the status fallback.
        throw Object.assign(
          new Error(detail ?? `HTTP ${response.status}: ${response.statusText}`),
          { status: response.status, detail },
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to execute REST request:", error);
      throw error;
    }
  }

  /**
   * Fetch Alfred's supported pairs / chains / fiats. Cached server-side
   * (30 min TTL) so this is cheap to call on widget mount.
   */
  public async getConfigs(): Promise<AlfredAllConfigs> {
    return this.request<AlfredAllConfigs>("GET", AlfredEndpoints.CONFIGS);
  }

  /**
   * Pre-mount gate for the deposit/withdrawal forms. Returns
   * `{eligible: true}` when the user can transact and
   * `{eligible: false, code}` when they can't — `code` is one of
   * `ALFRED_NOT_AVAILABLE_FOR_COUNTRY`, `ALFRED_CUSTOMER_NOT_PROVISIONED`,
   * `ALFRED_KYC_PENDING`, `ALFRED_KYC_REJECTED`, `USER_NOT_VERIFIED`.
   * Errors are surfaced as result codes (not thrown) so the host page
   * can branch without try/catch noise.
   */
  public async getEligibility(): Promise<AlfredEligibility> {
    const url = `${this.apiUrl.replace("/graphql", "")}${AlfredEndpoints.ELIGIBILITY}`;
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
        ...(this.authorization ? { Authorization: this.authorization } : {}),
      },
    });
    const body = await response.json().catch(() => ({}));
    // The KYC_UPDATE_REQUIRED case is returned as a *200* with
    // `{ eligible:false, reason, fallback }` (not a thrown 4xx like
    // PENDING/REJECTED), so a bare `response.ok` check would skip the
    // fallback entirely. Clear the gate only when the body isn't an
    // explicit `eligible:false`.
    if (response.ok && body?.eligible !== false) return { eligible: true };
    // `code` is historically carried in `message`; the newer
    // KYC_UPDATE_REQUIRED case surfaces it in `reason` alongside a
    // `fallback` schema. Read both so either BE shape resolves the code.
    const code =
      (typeof body.reason === "string" && body.reason) ||
      (typeof body.message === "string" && body.message) ||
      "UNKNOWN_ERROR";

    const rawFallback = body.fallback;
    const isFallback =
      code.startsWith("KYC_UPDATE_REQUIRED") &&
      rawFallback &&
      Array.isArray(rawFallback.fields) &&
      Array.isArray(rawFallback.documents);

    if (isFallback) {
      const fallback: KycFallback = {
        country: rawFallback.country,
        fields: rawFallback.fields,
        documents: rawFallback.documents,
      };
      return { eligible: false, code, fallback };
    }

    return { eligible: false, code };
  }

  /**
   * Submit the KYC update-required correction form. Multipart body: text
   * fields go in a JSON `corrections` part, each document is its own file
   * part keyed by its `fieldName`. File parts use ASCII-only filenames so
   * edge WAF rules do not reject Cyrillic/non-Latin Content-Disposition
   * names before the request reaches vakotrade. Sent via `fetch` directly
   * (NOT through `request`) so FormData stays untouched — JSON-stringifying
   * would break the upload and the browser must set the multipart boundary
   * itself, so no `Content-Type` header is forced.
   */
  public async submitKycFallback(
    payload: SubmitKycFallbackPayload,
  ): Promise<SubmitKycFallbackResponse> {
    const url = `${this.apiUrl.replace("/graphql", "")}${AlfredEndpoints.KYC_FALLBACK}`;
    const formData = new FormData();
    formData.append("corrections", JSON.stringify(payload.corrections));
    Object.entries(payload.documents).forEach(([fieldName, file]) => {
      formData.append(fieldName, file, toAsciiUploadFilename(fieldName, file));
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "ngrok-skip-browser-warning": "true",
        ...(this.authorization ? { Authorization: this.authorization } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let detail: string | null = null;
      try {
        const parsed = JSON.parse(errorBody) as { message?: string };
        detail = parsed?.message ?? null;
      } catch {
        // not JSON — keep raw text
      }
      throw new Error(detail ?? `HTTP ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as SubmitKycFallbackResponse;
  }

  /**
   * List every fiat the user can pick for this route in the given
   * direction (`deposit` → onramp, `withdrawal` → offramp). Each option
   * carries its own rail + min/max so the form can validate inline
   * without re-querying when the user changes the picker value.
   */
  public async getSupportedOptions(params: {
    paymentRouteId: string;
    type: "deposit" | "withdrawal";
  }): Promise<AlfredSupportedOptionsResponse> {
    return this.request<AlfredSupportedOptionsResponse>(
      "GET",
      AlfredEndpoints.SUPPORTED_OPTIONS,
      {
        params: {
          payment_route_id: params.paymentRouteId,
          type: params.type,
        },
      }
    );
  }

  /**
   * Live preview-quote: shows the user the stable-equivalent + Alfred
   * fee breakdown while they're typing. NOT bound to a session — a
   * fresh quote is requested at /onramp time on submit.
   */
  public async getQuotePreview(payload: QuotePreviewPayload): Promise<AlfredQuote> {
    return this.request<AlfredQuote>("POST", AlfredEndpoints.QUOTE_PREVIEW, {
      body: payload,
    });
  }

  /**
   * Start an Alfred onramp (deposit) and get back a redirect URL the
   * user should be sent to.
   */
  public async initiateDeposit(payload: InitiateDepositPayload): Promise<InitiateDepositResponse> {
    return this.request<InitiateDepositResponse>("POST", AlfredEndpoints.INITIATE_DEPOSIT, { body: payload });
  }

  /**
   * Submit an Alfred offramp (withdrawal) for an existing payment.
   */
  public async initiateWithdrawal(payload: InitiateWithdrawalPayload): Promise<InitiateWithdrawalResponse> {
    return this.request<InitiateWithdrawalResponse>("POST", AlfredEndpoints.INITIATE_WITHDRAWAL, { body: payload });
  }

  /**
   * Read-only snapshot of the cached widget session — used to poll
   * for transaction status updates while the user is in the SPEI/PIX
   * waiting screen. Returns `null` when the session has expired so
   * the UI can show a "session expired" state instead of looping.
   */
  public async getSession(sessionId: string) {
    return this.request<import("./types").AlfredSessionSnapshot | null>("GET", AlfredEndpoints.SESSION, {
      params: { session_id: sessionId },
    });
  }

  /**
   * Dev/QA-only — runs a preset scenario (`success` / `failure`) for
   * the current session. Backend dispatches the canonical chain
   * in-process with pauses so polling sees state progression. Server
   * enforces the env guard (VAKOTEST + *UAT only) so calling this
   * outside dev just 4xxs.
   */
  public async devEmitOnramp(payload: { session_id: string; scenario: "success" | "failure" }) {
    return this.request<{ session_id: string; transactionId: string; scenario: string }>(
      "POST",
      AlfredEndpoints.DEV_EMIT_ONRAMP,
      { body: payload }
    );
  }

  /**
   * Dev/QA-only counterpart to {@link devEmitOnramp} — walks the
   * canonical OFFRAMP webhook chain. `initiateWithdrawal` already
   * auto-runs the success chain on dev exchanges, so this is mainly
   * useful for re-running or testing the FAILED path from the QA
   * panel.
   */
  public async devEmitOfframp(payload: { session_id: string; scenario: "success" | "failure" }) {
    return this.request<{ session_id: string; transactionId: string; scenario: string }>(
      "POST",
      AlfredEndpoints.DEV_EMIT_OFFRAMP,
      { body: payload }
    );
  }

  /** Discovery list for the QA panel — KYC / ONRAMP / OFFRAMP statuses. */
  public async getDevStatuses() {
    return this.request<import("./types").AlfredDevStatusesResponse>("GET", AlfredEndpoints.DEV_STATUSES);
  }

  /**
   * List the customer's Alfred Payment Methods (registered bank
   * accounts) for the destination picker step.
   */
  public async listPaymentMethods(sessionId: string): Promise<AlfredPaymentMethod[]> {
    return this.request<AlfredPaymentMethod[]>("GET", AlfredEndpoints.PAYMENT_METHODS, {
      params: { session_id: sessionId },
    });
  }

  /**
   * Register a new bank account at Alfred for the customer behind a
   * widget session. Returns the created method (typically `pending`
   * until Alfred verifies it).
   */
  public async createPaymentMethod(payload: CreatePaymentMethodPayload): Promise<AlfredPaymentMethod> {
    return this.request<AlfredPaymentMethod>("POST", AlfredEndpoints.PAYMENT_METHODS, {
      body: payload,
    });
  }

  /**
   * Remove a previously registered bank account.
   */
  public async deletePaymentMethod(sessionId: string, paymentMethodId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>("DELETE", `${AlfredEndpoints.PAYMENT_METHODS}/${paymentMethodId}`, {
      params: { session_id: sessionId },
    });
  }

  /**
   * Backwards-compat adapter: returns the customer's payment methods
   * mapped onto the legacy `Bank` shape that the existing withdrawal
   * step components consume. New code should call
   * {@link listPaymentMethods} instead.
   *
   * @param params.exchange — kept for signature compatibility, unused
   *   server-side once the session is resolved.
   */
  public async getBanksList(params: GetBanksParams & { session_id?: string }): Promise<Bank[]> {
    if (!params.session_id) return [];
    const methods = await this.listPaymentMethods(params.session_id);
    return methods.map(paymentMethodToBank);
  }

  /**
   * Beneficiary FiatAccount controller — Alfred's newer offramp-recipient
   * registration flow alongside `payment-methods`. Per-rail required
   * fields are exposed via {@link getFiatAccountRequirements} so the
   * "Add bank account" form can render dynamically without a widget
   * redeploy when Alfred onboards a new rail.
   */
  public async listFiatAccounts(sessionId: string): Promise<AlfredFiatAccount[]> {
    return this.request<AlfredFiatAccount[]>("GET", AlfredEndpoints.FIAT_ACCOUNTS, {
      params: { session_id: sessionId },
    });
  }

  public async createFiatAccount(payload: CreateFiatAccountPayload): Promise<AlfredFiatAccount> {
    return this.request<AlfredFiatAccount>("POST", AlfredEndpoints.FIAT_ACCOUNTS, {
      body: payload,
    });
  }

  public async getFiatAccount(sessionId: string, fiatAccountId: string): Promise<AlfredFiatAccount> {
    return this.request<AlfredFiatAccount>("GET", `${AlfredEndpoints.FIAT_ACCOUNTS}/${fiatAccountId}`, {
      params: { session_id: sessionId },
    });
  }

  public async deleteFiatAccount(
    sessionId: string,
    fiatAccountId: string,
  ): Promise<AlfredDeleteFiatAccountResponse> {
    return this.request<AlfredDeleteFiatAccountResponse>(
      "DELETE",
      `${AlfredEndpoints.FIAT_ACCOUNTS}/${fiatAccountId}`,
      { params: { session_id: sessionId } },
    );
  }

  /**
   * Stateless metadata — per-rail required-fields schema. Cache on the
   * widget side (no auth, mostly static). Without `country` the BE returns
   * every supported country keyed by alpha-2.
   */
  public async getFiatAccountRequirements(
    country: AlfredFiatAccountRequirementCountry,
  ): Promise<AlfredFiatAccountRequirements>;
  public async getFiatAccountRequirements(): Promise<
    Record<AlfredFiatAccountRequirementCountry, AlfredFiatAccountRequirements>
  >;
  public async getFiatAccountRequirements(country?: AlfredFiatAccountRequirementCountry) {
    return this.request("GET", AlfredEndpoints.FIAT_ACCOUNT_REQUIREMENTS, {
      params: country ? { country } : undefined,
    });
  }
}

export const alfredService = new AlfredClient(getAlfredApiUrl());
